import { action, computed, makeObservable } from 'mobx';
import {ContentLookup, RoleName, TimeZone, User, UserUpdateModel} from '../../../domain';
import {
  Control,
  newValueForSelection,
  StringControl,
  toInt,
  validators
} from '../../../lib/helpers';
import {SelectLevelStore} from './SelectLevelStore';

export class UserEditFormStore {
  id: number;
  firstName: StringControl;
  lastName: StringControl;
  username: StringControl;
  password: StringControl;
  email: StringControl;
  mobile: StringControl;
  timeZone: Control<TimeZone | null>;
  roles: Control<RoleName[]>;
  status: string;
  CreationDateTime: number;

  student: {
    balance: StringControl;
    selectLevel: SelectLevelStore;
    alfaStudentId: StringControl;
  };

  teacher: {
    description: StringControl;
    alfaTeacherId: StringControl;
  };

  constructor(readonly role: 'create' | 'edit',
              readonly lookup: ContentLookup,
              readonly timeZones: readonly TimeZone[]) {
    makeObservable(this);
    this.id = 0;
    this.firstName = new StringControl('firstName', '', validators.required);
    this.lastName = new StringControl('lastName', '', validators.required);
    this.username = new StringControl('username', '', validators.required);
    this.email = new StringControl('email', '', validators.required);
    this.mobile = new StringControl('mobile', '', validators.required);
    this.password = new StringControl('', '',
      role === 'create' ? validators.required : null);
    this.timeZone = new Control<TimeZone | null>(
      'timeZone', null, validators.required);
    this.roles = new Control<RoleName[]>('roles', []);

    this.student = {
      balance: new StringControl('balance', '',
        [validators.required, validators.intString()]),
      selectLevel: new SelectLevelStore(lookup),
      alfaStudentId: new StringControl('alfaStudentId', '',
        [validators.required, validators.intString()]),
    };

    this.teacher = {
      description: new StringControl('teacher', '', validators.required),
      alfaTeacherId: new StringControl('alfaTeacherId', '',
        [validators.required, validators.intString()]),
    };
    this.status = "";
    this.CreationDateTime = 0;
  }

  hasRole = (roleName: RoleName) => {
    return this.roles.value.indexOf(roleName) !== -1;
  };

  @computed
  get isTeacher(): boolean {
    return this.hasRole('Teacher');
  }

  @computed
  get isStudent(): boolean {
    return this.hasRole('Student');
  }

  @computed
  get invalid(): boolean {
    if (this.firstName.invalid ||
      this.lastName.invalid ||
      this.username.invalid ||
      this.email.invalid ||
      this.password.invalid)
      return true;

    if (this.hasRole('Student'))
      if (this.student.balance.invalid ||
        this.student.selectLevel?.level.invalid)
        return true;

    if (this.hasRole('Teacher'))
      if (this.teacher.description.invalid)
        return true;

    return false;
  }

  @action
  setUser(user: User) {
    this.id = user.Id;
    this.firstName.setValue(user.FirstName);
    this.lastName.setValue(user.LastName);
    this.username.setValue(user.Username);
    this.email.setValue(user.Email);
    this.timeZone.setValue(newValueForSelection(this.timeZones, user.TimeZone));
    this.roles.setValue(user.Roles.slice());
    this.mobile.setValue(user.Mobile);

    if (user.Student !== null) {
      this.student.balance.setValue(user.Student.Balance.toString(10));
      this.student.selectLevel?.setLevel(user.Student.Level);
      this.student.alfaStudentId.setValue(user.Student.AlfaStudentId.toString(10));
    }
    if (user.Teacher !== null) {
      this.teacher.description.setValue(user.Teacher.Description);
    }

    this.status = user.Status;
    this.CreationDateTime = user.CreationDateTime;
  }

  @computed
  get model(): UserUpdateModel {
    const result: UserUpdateModel = {
      FirstName: this.firstName.value,
      LastName: this.lastName.value,
      Username: this.username.value,
      Email: this.email.value,
      Mobile: this.mobile.value,
      Password: this.password.value,
      TimeZoneId: this.timeZone.value!.Id,
      Roles: this.roles.value,
      Student: null,
      Teacher: null
    };

    if (this.isStudent) {
      const level = this.student.selectLevel.level.value;
      result.Student = {
        Balance: toInt(this.student.balance.value)!,
        LevelId: (level === null) ? null : level.Id,
        AlfaStudentId: toInt(this.student.alfaStudentId.value)
      };
    }

    if (this.isTeacher) {
      result.Teacher = {
        Description: this.teacher.description.value,
        AlfaTeacherId: toInt(this.teacher.alfaTeacherId.value)
      };
    }

    return result;
  }

  @computed
  get rawValue(): any {
    return [
      this.firstName.value,
      this.lastName.value,
      this.username.value,
      this.email.value,
      this.timeZone.value,
      this.roles.value,
      this.student.balance.value,
      this.student.selectLevel.level.value,
      this.teacher.description.value
    ];
  }

  @action
  markAllTouched = () => {
    [
      this.firstName,
      this.lastName,
      this.username,
      this.email,
      this.roles,
      this.student.balance,
      this.student.selectLevel.language,
      this.student.selectLevel.ageCategory,
      this.student.selectLevel.level,
      this.teacher.description]
      .forEach(x => x.markTouched());
  };
}
